import React, { memo, useRef, useEffect, useState, useCallback } from 'react';
import { dates, formats, validators, arrays } from 'investira.sdk';
import { Stack, Card, Icon } from 'investira.react.components';
import { StocksChart } from 'investira.react.charts';
import { COTACAO_TIPO } from '@investirapri/financa.js/lib/enums';
import Controls from './Controls';

const IndicadoresCotacao = memo(props => {
  const { indicador, cotacoes, cenarios, isPageLoading } = props;
  const chartRef = useRef(null);

  function formatData(pData = [], pAjustado = true, pType = 0) {
    if (validators.isNull(pData)) {
      return [];
    }

    const xCotacao = 'rentabilidade';
    const xTypes = [xCotacao];

    const xFormattedData = pData.map(xPoint => {
      const xDate = dates.toDate(xPoint.data).getTime();
      const xType = xTypes[pType];
      return {
        x: xDate,
        y: xPoint[xType],
        metadata: {
          ...xPoint
        }
      };
    });

    return xFormattedData;
  }

  const createChartOptions = useCallback(
    (pCotacoes, pAjustado = true, pType = 0) => {
      const to = new Date().getTime();
      const from = new Date(to - 365 * 24 * 3600 * 1000).getTime(); // 1 ano

      const xCotacaoTipo = arrays.arrayToObject(Object.values(COTACAO_TIPO), 'id');

      const xSeries = pCotacoes.map((pCotacao, pIndex) => {
        const xData = pCotacao.data;

        return {
          // Apenas a primeira série precisa ter id para os flags funcionarem
          ...(pIndex === 0 && { id: 'historico' }),
          name: pCotacao.indicador,
          data: formatData(xData, pAjustado, pType),
          connectEnds: true,
          type: 'line'
        };
      });

      // Adiciona flags apenas se houver cenários válidos e pelo menos uma série
      if (!validators.isNull(cenarios) && !validators.isEmpty(cenarios) && xSeries.length > 0) {
        const xFlagsData = cenarios
          .filter(xItem => !validators.isNull(xItem.inicio))
          .map(xItem => {
            const { cenario, descricao, inicio, fim, cenario_id } = xItem;
            const xDate = dates.toDate(inicio);
            if (isNaN(xDate.getTime())) {
              return null;
            }
            return {
              x: xDate.getTime(),
              title: cenario || '',
              text: descricao || '',
              // Inclui dados adicionais do cenário para uso no tooltip
              metadata: {
                cenario_id: cenario_id || '',
                inicio: inicio || '',
                fim: fim || '',
                cenario: cenario || '',
                descricao: descricao || ''
              }
            };
          })
          .filter(xItem => !validators.isNull(xItem));

        // Só adiciona flags se houver dados válidos
        if (xFlagsData.length > 0) {
          // Garante que a primeira série tenha um id para os flags funcionarem
          if (!xSeries[0].id) {
            xSeries[0].id = 'historico';
          }

          const xFlags = {
            type: 'flags',
            //onSeries: xSeries[0].id, // Usa o id da primeira série
            shape: 'squarepin',
            name: 'Cenários',
            allowOverlapX: true,
            boostThreshold: 0, // Desabilita boost para flags
            zIndex: 100, // Garante que os flags apareçam acima das outras séries
            visible: true, // Garante que os flags estejam visíveis
            data: xFlagsData,
            // Customização do tooltip
            tooltip: {
              shared: false, // Tooltip específico para flags, não compartilhado
              // Use formatter para controle total do tooltip
              formatter: function () {
                // No contexto do formatter, this.point se refere ao ponto do flag
                const xPoint = this.point;
                const xMetadata = xPoint.metadata || {};
                const xTitle = xPoint.title || '';
                const xText = xPoint.text || '';
                const xInicio = xMetadata.inicio
                  ? formats.formatDateCustom(xMetadata.inicio, 'DD/MMM/YYYY')
                  : '';
                const xFim = xMetadata.fim
                  ? formats.formatDateCustom(xMetadata.fim, 'DD/MMM/YYYY')
                  : '';
                const xPeriodo = xFim && xInicio !== xFim ? `${xInicio} - ${xFim}` : xInicio;

                let xTooltipContent = `<b>${xTitle}</b><br/>`;

                if (xText) {
                  xTooltipContent += `<span style="color: #666;">${xText}</span><br/>`;
                }

                if (xPeriodo) {
                  xTooltipContent += `<span style="color: #999; font-size: 0.9em;">Período: ${xPeriodo}</span><br/>`;
                }

                if (xMetadata.cenario_id) {
                  xTooltipContent += `<span style="color: #999; font-size: 0.85em;">ID: ${xMetadata.cenario_id}</span>`;
                }

                return xTooltipContent;
              }
            }
          };

          xSeries.push(xFlags);
        }
      }

      console.log('xSeries', xSeries);

      const xOptions = {
        title: false,
        chart: {
          zooming: {
            type: 'x'
          },
          events: {
            load: function () {
              const chart = this;
              chart.xAxis[0].setExtremes(from, to);
            }
          }
        },
        rangeSelector: {
          enabled: false,
          buttons: [
            {
              type: 'month',
              count: 1,
              text: '1M'
            },
            {
              type: 'month',
              count: 6,
              text: '6M'
            },
            {
              type: 'year',
              count: 1,
              text: '1A'
            },
            {
              type: 'year',
              count: 2,
              text: '2A'
            },
            {
              type: 'year',
              count: 3,
              text: '3A'
            },
            {
              type: 'year',
              count: 5,
              text: '5A'
            },
            {
              type: 'year',
              count: 10,
              text: '10A'
            },
            {
              type: 'ytd',
              text: 'YTD'
            },
            {
              type: 'all',
              text: 'MÁX.'
            }
          ],
          selected: 2
        },
        xAxis: { type: 'datetime', crosshair: true },
        yAxis: {
          type: 'linear',
          opposite: false,
          title: false,
          labels: {
            formatter: function () {
              return `${this.value}%`;
            }
          }
        },
        plotOptions: {
          series: {
            //gapSize: 7,
          },
          flags: {
            shape: 'squarepin',
            fillColor: '#ffffff',
            allowOverlapX: true,
            zIndex: 100 // Garante que os flags apareçam acima das outras séries
          }
        },
        series: xSeries,
        boost: {
          useGPUTranslations: true
        },
        tooltip: {
          split: false,
          crosshairs: true,
          shared: true,
          formatter: function () {
            // Verifica se algum ponto é um flag (tipo flags)
            const xHasFlag = this.points?.some(pPoint => pPoint.series?.type === 'flags');

            // Se for um flag, usa o formatter específico do flag
            if (xHasFlag) {
              const xFlagPoint = this.points.find(pPoint => pPoint.series?.type === 'flags');
              if (xFlagPoint) {
                const xMetadata = xFlagPoint.point.metadata || {};
                const xInicio = xMetadata.inicio
                  ? formats.formatDateCustom(xMetadata.inicio, 'DD/MMM/YYYY')
                  : '';
                const xFim = xMetadata.fim
                  ? formats.formatDateCustom(xMetadata.fim, 'DD/MMM/YYYY')
                  : '';
                const xPeriodo = xFim && xInicio !== xFim ? `${xInicio} - ${xFim}` : xInicio;

                let xTooltipContent = `<b>${xFlagPoint.point.title || ''}</b><br/>`;

                if (xFlagPoint.point.text) {
                  xTooltipContent += `<span style="color: #666;">${xFlagPoint.point.text}</span><br/>`;
                }

                if (xPeriodo) {
                  xTooltipContent += `<span style="color: #999; font-size: 0.9em;">Período: ${xPeriodo}</span><br/>`;
                }

                if (xMetadata.cenario_id) {
                  xTooltipContent += `<span style="color: #999; font-size: 0.85em;">ID: ${xMetadata.cenario_id}</span>`;
                }

                return xTooltipContent;
              }
            }

            const xDate = formats.formatDateCustom(this.point.x, 'DD/MMM/YYYY');

            let xTooltipContent = `<span style="font-size: 1em"><b>${xDate}</b></span><br/>`;

            const xIndicadorTipoId = this.points
              ? this.points[0].point.metadata?.indicador_tipo_id
              : null;

            if (xIndicadorTipoId === 'M') {
              this.points.forEach(function (point) {
                const xRentabilidade = formats.formatNumber(point.y, 2, false);
                xTooltipContent += `<span style="color:${point.color}">\u25CF</span>Variação Acumulada: <b>${xRentabilidade}%</b><br/>`;
                xTooltipContent += `<span style="color:${
                  point.color
                }">\u25CF</span>Cotação: <b>${formats.formatNumber(
                  point.point.metadata.cotacao,
                  4,
                  true,
                  true
                )}</b><br/>`;
              });

              return xTooltipContent;
            } else {
              this.points &&
                this.points.forEach(function (point) {
                  const xRentabilidade = formats.formatNumber(point.y, 2, false);
                  xTooltipContent += `<span style="color:${point.color}">\u25CF</span>Variação Acumulada: <b>${xRentabilidade}%</b><br/>`;
                  xTooltipContent += point.point.metadata.cotacao
                    ? `<span style="color:${point.color}">\u25CF</span>Cotação: <b>R$ ${point.point.metadata.cotacao}</b><br/>`
                    : '';
                  xTooltipContent += `<span style="color:${point.color}">\u25CF</span>Taxa: <b>${
                    point.point.metadata.taxa || '0'
                  }%</b><br/>`;
                  xTooltipContent += `<span style="color:${
                    point.color
                  }">\u25CF</span>Data Divulgação: <b>${formats.formatDateCustom(
                    point.point.metadata.data,
                    'DD/MMM/YYYY'
                  )}</b><br/>`;
                  //xTooltipContent += `<hr/>`
                  xTooltipContent += `<span style="color:${
                    point.color
                  }">\u25CF</span>Cotação Tipo:  <b>${
                    xCotacaoTipo[point.point.metadata.cotacao_tipo_id].descricao
                  }</b><br/>`;
                });

              return xTooltipContent;
            }
          }
        }
      };

      return xOptions;
    },
    [cenarios]
  );

  const [ajustado, setAjustado] = useState(false);
  const [type, setType] = useState(0);
  const [options, setOptions] = useState(null);

  const handleRangeSelect = (type, count) => {
    const xChart = chartRef.current;
    const xTo = new Date().getTime();
    let xFrom;

    switch (type) {
      case 'month':
        xFrom = new Date(xTo - count * 30 * 24 * 3600 * 1000).getTime();
        break;
      case 'year':
        xFrom = new Date(xTo - count * 365 * 24 * 3600 * 1000).getTime();
        break;
      case 'ytd':
        const startOfYear = new Date(new Date().getFullYear(), 0, 1).getTime();
        xFrom = startOfYear;
        break;
      case 'all':
        xFrom = null;
        break;
      default:
        return;
    }

    xChart.xAxis[0].setExtremes(xFrom, xTo);
  };

  useEffect(() => {
    const xOptions = createChartOptions(cotacoes, ajustado, type);

    setOptions(xOptions);
  }, [cotacoes, ajustado, type, createChartOptions]);

  if (validators.isEmpty(cotacoes[0].data)) {
    return null;
  }

  if (validators.isNull(options)) {
    return null;
  }

  return (
    <Stack>
      <Card>
        <Stack>
          <Stack
            flexGrow={1}
            py={2}
            px={2}
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}>
            <Stack>
              <Icon iconName="chart_line" color="primary" size={21} />
            </Stack>
            <Stack flexGrow="1"></Stack>
            <Controls
              range={{
                selected: options.rangeSelector.selected,
                buttons: options.rangeSelector.buttons,
                setRange: handleRangeSelect
              }}
              ajustado={{
                onChange: setAjustado
              }}
              type={{
                value: type,
                onChange: setType
              }}
            />
          </Stack>
          <Stack sx={{ px: 2, py: 2 }}>
            <StocksChart key={'cotacao'} ref={chartRef} options={options} />
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
});

IndicadoresCotacao.displayName = 'IndicadoresCotacao';

export default IndicadoresCotacao;
